import { Capacitor } from "@capacitor/core";
import { Filesystem, Directory } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";
import { UPLOAD_URL } from "../utils/constants";
import { shareViaNative } from "./nativeShare";
import { openWhatsAppChatDirect } from "./whatsapp.helper";

const uploadsBase = UPLOAD_URL.replace(/\/+$/, "");
const MAX_WHATSAPP_IMAGE_ATTACHMENTS = 1;

const cleanValue = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  if (Array.isArray(value)) {
    return value
      .map((entry) => cleanValue(entry))
      .filter(Boolean)
      .join(", ");
  }
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  const text = String(value).trim();
  if (
    !text ||
    text.toLowerCase() === "null" ||
    text.toLowerCase() === "undefined"
  ) {
    return "";
  }
  return text;
};

const normalizeLabel = (label: string): string =>
  label.replace(/([a-z])([A-Z])/g, "$1 $2");

const normalizeDisplayValue = (value: unknown, fallback = "N/A"): string => {
  const text = cleanValue(value);
  return text || fallback;
};

const joinNonEmpty = (...values: unknown[]): string =>
  values
    .map((value) => cleanValue(value))
    .filter(Boolean)
    .join(", ");

const buildRows = (rows: Array<[string, unknown]>): string =>
  rows
    .map(([label, value]) => {
      const text = cleanValue(value);
      if (!text) return "";
      return `<tr><td><strong>${normalizeLabel(label)}</strong></td><td>${text}</td></tr>`;
    })
    .filter(Boolean)
    .join("");

const buildSection = (
  title: string,
  rows: Array<[string, unknown]>,
): string => {
  const tableRows = buildRows(rows);
  if (!tableRows) return "";
  return `
    <section>
      <h2>${title}</h2>
      <table>${tableRows}</table>
    </section>
  `;
};

const formatCreatedBy = (value: unknown): string => {
  const text = cleanValue(value);
  if (!text) return "";
  const normalized = text.toLowerCase().replace(/[\s-]+/g, "_");
  const labels: Record<string, string> = {
    self: "Self",
    father: "Father",
    mother: "Mother",
    brother: "Brother",
    sister: "Sister",
    friend: "Friend",
    relatives: "Relatives",
    super_admin: "Super Admin",
    other_mediator: "Other Mediator",
    admin: "Admin",
  };
  return (
    labels[normalized] ||
    normalized.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase())
  );
};

const formatDateToDDMMYYYY = (value: unknown): string => {
  const text = cleanValue(value);
  if (!text) return "";

  const parsed = new Date(text);
  if (Number.isNaN(parsed.getTime())) {
    return text;
  }

  const day = String(parsed.getDate()).padStart(2, "0");
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const year = parsed.getFullYear();
  return `${day}/${month}/${year}`;
};

const formatCurrencyForShare = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "";

  if (typeof value === "string") {
    const text = value.trim();
    if (!text) return "";
    if (/[a-z]/i.test(text)) {
      return text
        .replace(/\s+/g, " ")
        .replace(/\bcrore(s)?\b/gi, (_, suffix) =>
          suffix ? "Crores" : "Crore",
        )
        .replace(/\blakh(s)?\b/gi, (_, suffix) => (suffix ? "Lakhs" : "Lakh"));
    }
  }

  const numericValue =
    typeof value === "number"
      ? value
      : Number(String(value).replace(/[^\d.-]/g, ""));

  if (!Number.isFinite(numericValue) || numericValue <= 0) {
    return "";
  }

  if (numericValue >= 10000000) {
    const crores = numericValue / 10000000;
    return `${Number(crores.toFixed(crores % 1 === 0 ? 0 : 2))} Crores`;
  }

  const lakhs = numericValue / 100000;
  return `${Number(lakhs.toFixed(lakhs % 1 === 0 ? 0 : 2))} Lakhs`;
};

const formatLocation = (profile: Record<string, any>): string =>
  joinNonEmpty(
    profile?.selectedLocation || profile?.location,
    profile?.city,
    profile?.district,
    profile?.state,
    profile?.country,
  );

const buildAboutText = (profile: Record<string, any>): string => {
  const summary = Array.isArray(profile?.customWords)
    ? profile.customWords
        .map((entry: unknown) => cleanValue(entry))
        .filter(Boolean)
        .join(" ")
    : cleanValue(profile?.customWords);

  const description = cleanValue(profile?.profileDescription);
  return joinNonEmpty(summary, description);
};

const formatOptionalCount = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "";

  const numericValue =
    typeof value === "number" ? value : Number(String(value).trim());

  if (Number.isFinite(numericValue)) {
    return numericValue > 0 ? String(numericValue) : "";
  }

  return cleanValue(value);
};

const formatOptionalField = (value: unknown): string => {
  const text = cleanValue(value);
  if (!text) return "";

  const numericValue = Number(text);
  if (Number.isFinite(numericValue) && numericValue <= 0) {
    return "";
  }

  return text;
};

const familyRows = (profile: Record<string, any>): Array<[string, unknown]> => [
  ["Father Employee", profile?.fatherEmployee],
  ["Father Occupation", profile?.fatherOccupied || profile?.fatherOccupation],
  ["Mother Employee", profile?.motherEmployee],
  ["Mother Occupation", profile?.motherOccupied || profile?.motherOccupation],
  ["Family Status", profile?.familyStatus],
  ["Family Type", profile?.familyType],
  ["Family Value", profile?.familyValue],
  ["Total Brother", formatOptionalCount(profile?.totalBrothers)],
  ["Younger Brother", formatOptionalCount(profile?.youngerBrothers)],
  ["Elder Brother", formatOptionalCount(profile?.elderBrothers)],
  ["Married Brother", formatOptionalCount(profile?.marriedBrothers)],
  ["Total Sister", formatOptionalCount(profile?.totalSisters)],
  ["Younger Sister", formatOptionalCount(profile?.youngerSisters)],
  ["Elder Sister", formatOptionalCount(profile?.elderSisters)],
  ["Married Sister", formatOptionalCount(profile?.marriedSisters)],
  ["Proper Location", profile?.originalLocation],
  ["Shettild Location", profile?.selectedLocation || profile?.location],
];

const propertyRows = (
  profile: Record<string, any>,
): Array<[string, unknown]> => [
  ["Property Names", profile?.propertyNames],
  ["House Type", profile?.houseType],
  ["Number Of Houses", formatOptionalCount(profile?.numberOfHouses)],
  ["House Sq Yards", formatOptionalField(profile?.houseSqFeet)],
  ["House Value", formatCurrencyForShare(profile?.houseValue)],
  ["House Location", profile?.houseLocation],
  ["Monthly Rent", formatOptionalField(profile?.monthlyRent)],
  ["Open Plots", formatOptionalCount(profile?.openPlots)],
  ["Open Plots Sq Yards", formatOptionalField(profile?.openPlotsSqFeet)],
  ["Open Plots Value", formatCurrencyForShare(profile?.openPlotsValue)],
  ["Plot Location", profile?.openPlotsLocation],
  ["Commercial Property", formatOptionalCount(profile?.commercialshops)],
  ["Commercial Sq Yards", formatOptionalField(profile?.shopssqyards)],
  ["Flat Type", profile?.flatType],
  ["Number Of Flats", formatOptionalCount(profile?.numberOfFlats)],
  ["Flat Value", formatCurrencyForShare(profile?.flatValue)],
  ["Flat Location", profile?.flatLocation],
  ["Agriculture Land", formatOptionalCount(profile?.agricultureLand)],
  [
    "Agriculture Land Value",
    formatCurrencyForShare(profile?.agricultureLandValue),
  ],
  ["Agriculture Location", profile?.agricultureLandLocation],
  ["Any More Properties", profile?.anyMoreProperties],
  [
    "Total Property Value",
    formatCurrencyForShare(profile?.totalPropertiesValue),
  ],
];

const buildWhatsAppSection = (
  title: string,
  rows: Array<[string, unknown]>,
): string => {
  const lines = rows
    .map(([label, value]) => {
      const text = cleanValue(value);
      if (!text) return "";
      return `${label}: ${text}`;
    })
    .filter(Boolean);

  if (!lines.length) return "";
  return [`*${title}*`, ...lines].join("\n");
};

const normalizePhoto = (source: string, folder: "profile_pic" | "gallery") => {
  const trimmed = source.trim().replace(/^\/+/, "");
  if (!trimmed) return "";

  if (/^https?:\/\//i.test(trimmed)) return trimmed;

  if (trimmed.startsWith("uploads/users/")) {
    return `${uploadsBase}/${trimmed.replace(/^uploads\//, "")}`;
  }

  if (trimmed.startsWith("users/")) {
    return `${uploadsBase}/${trimmed}`;
  }

  const filename = trimmed.split("/").pop();
  return `${uploadsBase}/users/${folder}/${filename}`;
};

const normalizeFileUrl = (source: string) => {
  const trimmed = source.trim().replace(/^\/+/, "");
  if (!trimmed) return "";
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (trimmed.startsWith("uploads/")) {
    return `${uploadsBase}/${trimmed.replace(/^uploads\//, "")}`;
  }
  return `${uploadsBase}/${trimmed}`;
};

const getProfileImageUrls = (profile: Record<string, any>): string[] => {
  const profileImage = cleanValue(profile?.image);
  const galleryImages = Array.isArray(profile?.gallery) ? profile.gallery : [];

  return Array.from(
    new Set(
      [
        profileImage ? normalizePhoto(profileImage, "profile_pic") : "",
        ...galleryImages
          .map((entry) => cleanValue(entry))
          .filter(Boolean)
          .map((entry) => normalizePhoto(entry, "gallery")),
      ].filter(Boolean),
    ),
  );
};

const getVoiceMessageUrl = (profile: Record<string, any>): string | null => {
  const value = cleanValue(profile?.voiceMessage);
  if (!value) return null;
  return normalizeFileUrl(value);
};

const openWhatsAppShareUrl = (text: string) => {
  const encodedText = encodeURIComponent(text);
  const isMobileBrowser =
    typeof navigator !== "undefined" &&
    /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

  const whatsappUrl = isMobileBrowser
    ? `whatsapp://send?text=${encodedText}`
    : `https://web.whatsapp.com/send?text=${encodedText}`;

  window.open(whatsappUrl, "_blank", "noopener,noreferrer");
};

export const shareVoiceMessageOnWhatsApp = async (
  voiceMessage: string,
  options?: { profileName?: string; profileId?: string },
): Promise<void> => {
  const audioUrl = normalizeFileUrl(voiceMessage);

  if (!audioUrl) {
    throw new Error("Voice message is not available to share.");
  }

  const audioUrlWithCacheBust = `${audioUrl}${
    audioUrl.includes("?") ? "&" : "?"
  }t=${Date.now()}`;

  const fileNameBase =
    cleanValue(options?.profileId) ||
    cleanValue(options?.profileName).replace(/\s+/g, "-").toLowerCase() ||
    "voice-message";

  const fileName = `${fileNameBase}-voice.aac`;

  const text = cleanValue(options?.profileName)
    ? `Voice message from ${cleanValue(options?.profileName)}`
    : "MeeMarriage voice message";

  if (Capacitor.isNativePlatform()) {
    const audioFiles = await collectSuccessfulResults([
      createNativeFile(audioUrlWithCacheBust, fileName, 1),
    ]);

    console.log("native audioFiles", audioFiles);

    if (!audioFiles.length) {
      throw new Error("Unable to prepare voice file for sharing.");
    }

    try {
      await shareViaNative({
        title: "MeeMarriage Voice Message",
        files: audioFiles,
        preferWhatsApp: true,
      });
      return;
    } catch (error) {
      console.warn("Native WhatsApp file share failed", error);
    }

    const shared = await tryNativeShare({
      title: "MeeMarriage Voice Message",
      text,
      files: audioFiles,
    });

    if (shared) return;

    throw new Error("Unable to share voice file.");
  }

  throw new Error("Voice file sharing is supported only in app.");
};

const appendVoiceMessageLink = (
  text: string,
  audioUrl?: string | null,
): string => {
  if (!audioUrl) return text;
  return `${text}\n\n*Voice Message*\n${audioUrl}`;
};

const tryNativeShare = async (payload: {
  title: string;
  text: string;
  files?: string[];
}): Promise<boolean> => {
  try {
    await Share.share({
      title: payload.title,
      text: payload.text,
      files: payload.files,
      dialogTitle: "Share via WhatsApp",
    });
    return true;
  } catch (error) {
    console.warn("Native share attempt failed", error);
    return false;
  }
};

const inferExtension = (blob: Blob, fallback = "bin") => {
  const mime = blob.type || "";
  if (mime.includes("jpeg")) return "jpg";
  if (mime.includes("png")) return "png";
  if (mime.includes("webp")) return "webp";
  if (mime.includes("mp3")) return "mp3";
  if (mime.includes("mpeg")) return "mp3";
  if (mime.includes("wav")) return "wav";
  if (mime.includes("ogg")) return "ogg";
  if (mime.includes("aac")) return "aac";
  if (mime.includes("audio/mp4") || mime.includes("m4a")) return "m4a";
  if (mime.includes("mp4")) return "mp4";
  if (mime.includes("webm")) return "webm";
  return fallback;
};

const sanitizeFilename = (value: string): string =>
  value.replace(/[^a-zA-Z0-9._-]/g, "_");

const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result?.toString() || "";
      resolve(result.split(",")[1] || "");
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const fetchBlob = async (url: string): Promise<Blob> => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Unable to fetch media: ${url}`);
  }
  return response.blob();
};

const createWebFile = async (
  url: string,
  filenameBase: string,
  index: number,
): Promise<File> => {
  const blob = await fetchBlob(url);
  const urlPath = (() => {
    try {
      return new URL(url).pathname.split("/").pop() || "";
    } catch {
      return "";
    }
  })();
  const extension = urlPath.includes(".")
    ? urlPath.split(".").pop() || inferExtension(blob)
    : inferExtension(blob);
  return new File(
    [blob],
    `${sanitizeFilename(filenameBase)}-${index}.${extension}`,
    {
      type: blob.type || undefined,
    },
  );
};

const createNativeFile = async (
  url: string,
  filenameBase: string,
  index: number,
): Promise<string> => {
  const blob = await fetchBlob(url);
  console.log("blob size", blob.size);
  console.log("blob type", blob.type);
  const extension = inferExtension(blob);
  const filename = `${sanitizeFilename(filenameBase)}-${Date.now()}-${index}.${extension}`;
  const base64 = await blobToBase64(blob);
  const write = await Filesystem.writeFile({
    path: filename,
    data: base64,
    directory: Directory.Cache,
  });
  console.log("write.uri", write.uri);
  return write.uri;
};

const collectSuccessfulResults = async <T>(
  tasks: Array<Promise<T>>,
): Promise<T[]> => {
  const results = await Promise.allSettled(tasks);
  return (
    results
      .filter(
        // @ts-ignore
        (result): result is PromiseFulfilledResult<T> =>
          result.status === "fulfilled",
      )
      //@ts-ignore
      .map((result) => result.value)
  );
};

export const buildProfilePublicUrl = (profile: Record<string, any>): string => {
  const bureauId = cleanValue(profile?.bureauId);
  const profileId = cleanValue(profile?.martialId || profile?.maritalId);
  const bureauSlug = cleanValue(profile?.bureauName).replace(/\s+/g, "");
  if (!bureauId || !profileId) return "";
  return `https://matrimonystudio.in/${encodeURIComponent(bureauId)}/${encodeURIComponent(bureauSlug || "MeeMarriage")}/${encodeURIComponent(profileId)}`;
};

export const buildProfileShareText = (
  profile: Record<string, any>,
  options?: { includePublicLink?: boolean },
): string => {
  const includePublicLink = options?.includePublicLink ?? true;
  const publicUrl = includePublicLink ? buildProfilePublicUrl(profile) : "";
  const sections = [
    buildWhatsAppSection("Profile Description", [
      ["", buildAboutText(profile)],
    ]),
    buildWhatsAppSection("Personal Details", [
      ["Name", profile?.fullName],
      ["Profile ID", profile?.martialId || profile?.maritalId],
      ["Gender", profile?.gender],
      ["Age", profile?.age],
      ["Date Of Birth", formatDateToDDMMYYYY(profile?.dateOfBirth)],
      ["Marital Status", profile?.maritalStatus],
      ["Height", profile?.height],
      ["Citizenship", profile?.citizenship],
      ["Created By", formatCreatedBy(profile?.createdBy)],
      ["Birth Time", profile?.birthTime || profile?.time],
      ["Weight", profile?.weight],
      ["Physical Status", profile?.physicalState],
      ["Eating Habits", profile?.eatingHabits],
      ["Smoking Habits", profile?.smokingHabits],
      ["Drinking Habits", profile?.drinkingHabits],
    ]),
    buildWhatsAppSection("Religion & Caste Details", [
      ["Religion", profile?.religion],
      ["Caste", profile?.caste],
      ["Sub Caste", profile?.subcaste],
      ["Spoken Languages", profile?.spokenLanguages || profile?.languagesKnown],
      ["Mother Tongue", profile?.motherTongue],
      ["Raashi", profile?.raasi],
      ["Star", profile?.star],
      ["Dosham", profile?.dosham],
    ]),
    buildWhatsAppSection("Education & Profession Details", [
      ["Education", profile?.education],
      ["Employee", profile?.employmentStatus],
      ["Occupation", profile?.occupation],
      ["Annual Income", profile?.annualIncome],
      ["Job Location", profile?.jobLocation],
      ["Any Other Business", profile?.otherBusiness],
      ["Business Income", profile?.otherBusinessIncome],
      ["Business Location", profile?.businessLocation],
      ["Extra Talented Skills", profile?.extraTalentedSkills],
    ]),
    buildWhatsAppSection("Family Details", familyRows(profile)),
    buildWhatsAppSection("Property Details", propertyRows(profile)),
    buildWhatsAppSection("Location Details", [
      ["Location", formatLocation(profile)],
    ]),
    publicUrl ? `*Public Link*\n${publicUrl}` : "",
  ].filter(Boolean);

  return sections.join("\n\n");
};

export const shareProfileOnWhatsApp = async (
  profile: Record<string, any>,
  options?: {
    includePublicLink?: boolean;
    phoneNumber?: string;
    message?: string;
  },
): Promise<void> => {
  const text = options?.message ?? buildProfileShareText(profile, options);

  const phoneNumber = options?.phoneNumber;

  // Native Android / iOS
  if (Capacitor.isNativePlatform()) {
    const fileNameBase =
      cleanValue(profile?.martialId || profile?.maritalId) ||
      cleanValue(profile?.fullName).replace(/\s+/g, "-").toLowerCase() ||
      "profile";

    const imageUrls = getProfileImageUrls(profile).slice(
      0,
      MAX_WHATSAPP_IMAGE_ATTACHMENTS,
    );

    const imageFiles = await collectSuccessfulResults(
      imageUrls.map(async (url, index) => {
        const file = await createNativeFile(
          url,
          `${fileNameBase}-image`,
          index + 1,
        );

        console.log("created file", file);

        return file;
      }),
    );

    // Existing image + text sharing
    try {
      await shareViaNative({
        title: "MeeMarriage Profile",
        text,
        files: imageFiles,
        preferWhatsApp: true,
      });

      return;
    } catch (error) {
      console.warn("Preferred WhatsApp native share failed", error);
    }

    const shared = await tryNativeShare({
      title: "MeeMarriage Profile",
      text,
      files: imageFiles,
    });

    if (shared) {
      return;
    }

    // Fallback: text only
    if (phoneNumber) {
      openWhatsAppChatDirect(phoneNumber, text);
    } else {
      openWhatsAppShareUrl(text);
    }

    return;
  }

  // Browser: text only
  if (phoneNumber) {
    openWhatsAppChatDirect(phoneNumber, text);
  } else {
    openWhatsAppShareUrl(text);
  }
};

export const downloadBiodataDocument = (profile: Record<string, any>): void => {
  const fileNameBase =
    cleanValue(profile?.martialId || profile?.maritalId) ||
    cleanValue(profile?.fullName).replace(/\s+/g, "-").toLowerCase() ||
    "profile";

  const aboutText = buildAboutText(profile);

  const html = `
    <html>
      <head>
        <meta charset="utf-8" />
        <title>${cleanValue(profile?.fullName) || "MeeMarriage Profile"} Biodata</title>
        <style>
          body { font-family: Arial, sans-serif; color: #111827; padding: 24px; }
          h1 { margin-bottom: 4px; }
          h2 { margin: 24px 0 12px; font-size: 18px; }
          p { margin: 0 0 8px; }
          table { width: 100%; border-collapse: collapse; }
          td { border: 1px solid #e5e7eb; padding: 10px; vertical-align: top; }
          td:first-child { width: 34%; background: #f9fafb; }
        </style>
      </head>
      <body>
        <h1>${cleanValue(profile?.fullName) || "MeeMarriage Profile"}</h1>
        <p><strong>Profile ID:</strong> ${cleanValue(profile?.martialId || profile?.maritalId) || "N/A"}</p>
        <p><strong>Public Link:</strong> ${buildProfilePublicUrl(profile) || "N/A"}</p>
        ${buildSection("Personal Details", [
          ["Gender", profile?.gender],
          ["Age", profile?.age],
          ["Date Of Birth", profile?.dateOfBirth],
          ["Marital Status", profile?.maritalStatus],
          ["Height", profile?.height],
          ["Citizenship", profile?.citizenship],
          ["Created By", formatCreatedBy(profile?.createdBy)],
          ["Birth Time", profile?.birthTime || profile?.time],
          ["Weight", profile?.weight],
          ["Physical Status", profile?.physicalState],
          ["Eating Habits", profile?.eatingHabits],
          ["Smoking Habits", profile?.smokingHabits],
          ["Drinking Habits", profile?.drinkingHabits],
        ])}
        ${buildSection("Religion & Caste Details", [
          ["Religion", profile?.religion],
          ["Mother Tongue", profile?.motherTongue],
          ["Caste", profile?.caste],
          ["Sub Caste", profile?.subcaste],
          [
            "Spoken Languages",
            profile?.spokenLanguages || profile?.languagesKnown,
          ],
          ["Star", profile?.star],
          ["Raashi", profile?.raasi],
          ["Dosham", profile?.dosham],
        ])}
        ${buildSection("Education & Profession Details", [
          ["Education", profile?.education],
          ["Employee", profile?.employmentStatus],
          ["Occupation", profile?.occupation],
          ["Annual Income", profile?.annualIncome],
          ["Job Location", profile?.jobLocation],
          ["Any Other Business", profile?.otherBusiness],
          ["Business Income", profile?.otherBusinessIncome],
          ["Business Location", profile?.businessLocation],
          ["Extra Talented Skills", profile?.extraTalentedSkills],
        ])}
        ${buildSection("Family Details", familyRows(profile))}
        ${buildSection("Property Details", propertyRows(profile))}
        ${buildSection("Location Details", [
          ["Country", profile?.country],
          ["State", profile?.state],
          ["City", profile?.city || profile?.district],
          ["Selected Location", profile?.selectedLocation],
        ])}
        ${aboutText ? `<section><h2>Profile Description</h2><p><strong>${aboutText}</strong></p></section>` : ""}
      </body>
    </html>
  `;

  const blob = new Blob([html], { type: "application/msword" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `${fileNameBase}-biodata.doc`;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};

export const getProfileShareSummary = (profile: Record<string, any>) => ({
  publicUrl: buildProfilePublicUrl(profile),
  imageCount: Math.min(
    getProfileImageUrls(profile).length,
    MAX_WHATSAPP_IMAGE_ATTACHMENTS,
  ),
  hasVoiceMessage: Boolean(getVoiceMessageUrl(profile)),
  location: normalizeDisplayValue(formatLocation(profile)),
});
